import React, { useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReportDetailPage } from '../ui/diary/ReportDetailPage';
import type { Report, ReportDetailLabels } from '../shared/types/reports';

// Create a query client for this component tree
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

export interface ReportDetailPageContainerProps {
  graphqlEndpoint: string;
  csrfToken: string;
  reportId: string;
  reportsListUrl: string;
  aiReportFullUrl: string;
  labels: ReportDetailLabels;
}

// GraphQL Query for single report
const GET_REPORT_QUERY = `
  query GetReport($id: ID!) {
    report(id: $id) {
      id
      periodType
      periodStart
      periodEnd
      generatedAt
      viewedAt
      unviewed
      moodChange
      moodCountChange
      currentPeriodStats {
        periodStart
        periodEnd
        averageMood
        moodCount
        fieldActivities {
          fieldId
          fieldName
          count
          averageMood
          categoryName
          fieldSetName
        }
        goals {
          fieldName
          targetCount
          actualCount
          period
          completionPercentage
          achieved
        }
        achievements {
          id
          name
          description
          icon
        }
        weekdayMoods
        moodTimeline {
          date
          mood
        }
        moodDistribution
        activityOccurrencePercentage
      }
      previousPeriodStats {
        periodStart
        periodEnd
        averageMood
        moodCount
        fieldActivities {
          fieldId
          fieldName
          count
          averageMood
          categoryName
          fieldSetName
        }
        moodTimeline {
          date
          mood
        }
      }
      aiReport {
        id
        content
        title
        firstSentence
        userFeedback {
          id
          feedbackType
          comments
        }
      }
      suggestions {
        activityCombos {
          bestCombos {
            field1Id
            field1Name
            field2Id
            field2Name
            averageMoodTogether
            averageMoodSeparate
            moodDifference
            coOccurrenceCount
            categoryName
          }
          worstCombos {
            field1Id
            field1Name
            field2Id
            field2Name
            averageMoodTogether
            averageMoodSeparate
            moodDifference
            coOccurrenceCount
            categoryName
          }
        }
        automationSuggestions {
          fieldId
          fieldName
          categoryId
          categoryName
          suggestionType
          occurrencePercentage
          details
        }
        toggleFieldConversions {
          activityName
          occurrenceCount
          firstOccurrence
          lastOccurrence
        }
        fieldSetGroupings {
          categoryId
          categoryName
          fieldIds
          fieldNames
          reason
          confidenceScore
        }
      }
    }
  }
`;

// Mutations
const MARK_REPORT_VIEWED_MUTATION = `
  mutation MarkReportViewed($input: MarkReportViewedInput!) {
    markReportViewed(input: $input) {
      report {
        id
        viewedAt
        unviewed
      }
    }
  }
`;

const SUBMIT_AI_REPORT_FEEDBACK_MUTATION = `
  mutation SubmitAiReportFeedback($input: SubmitAiReportFeedbackInput!) {
    submitAiReportFeedback(input: $input) {
      aiReport {
        id
        userFeedback {
          id
          feedbackType
          comments
        }
      }
    }
  }
`;

const DELETE_AI_REPORT_FEEDBACK_MUTATION = `
  mutation DeleteAiReportFeedback($input: DeleteAiReportFeedbackInput!) {
    deleteAiReportFeedback(input: $input) {
      aiReport {
        id
        userFeedback {
          id
          feedbackType
          comments
        }
      }
    }
  }
`;

function ReportDetailPageInner({
  graphqlEndpoint,
  csrfToken,
  reportId,
  reportsListUrl,
  aiReportFullUrl,
  labels,
}: ReportDetailPageContainerProps) {
  const qc = useQueryClient();

  // Fetch report data
  const { data: report, isLoading } = useQuery({
    queryKey: ['report', reportId],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: GET_REPORT_QUERY,
          variables: { id: reportId },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch report');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.report as Report;
    },
  });

  // Mark as viewed mutation
  const markViewedMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: MARK_REPORT_VIEWED_MUTATION,
          variables: { input: { reportId } },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark report as viewed');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.markReportViewed.report;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['report', reportId] });
      qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });

  // Submit feedback mutation
  const submitFeedbackMutation = useMutation({
    mutationFn: async ({ feedbackType, comments }: { feedbackType: string; comments?: string }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: SUBMIT_AI_REPORT_FEEDBACK_MUTATION,
          variables: {
            input: {
              aiReportId: report?.aiReport?.id,
              feedbackType,
              comments,
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.submitAiReportFeedback.aiReport;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['report', reportId] });
    },
  });

  // Delete feedback mutation
  const deleteFeedbackMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: DELETE_AI_REPORT_FEEDBACK_MUTATION,
          variables: {
            input: {
              aiReportId: report?.aiReport?.id,
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete feedback');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.deleteAiReportFeedback.aiReport;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['report', reportId] });
    },
  });

  // Mark as viewed on mount
  useEffect(() => {
    if (report?.unviewed) {
      markViewedMutation.mutate();
    }
  }, [report?.unviewed]);

  const handleSubmitFeedback = useCallback(
    async (feedbackType: 'dislike' | 'like' | 'super_like', comments?: string) => {
      await submitFeedbackMutation.mutateAsync({ feedbackType, comments });
    },
    [submitFeedbackMutation]
  );

  const handleDeleteFeedback = useCallback(async () => {
    await deleteFeedbackMutation.mutateAsync();
  }, [deleteFeedbackMutation]);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="alert alert-danger">
        Report not found
      </div>
    );
  }

  return (
    <ReportDetailPage
      report={report}
      reportsListUrl={reportsListUrl}
      aiReportFullUrl={aiReportFullUrl}
      onSubmitFeedback={handleSubmitFeedback}
      onDeleteFeedback={handleDeleteFeedback}
      labels={labels}
    />
  );
}

/**
 * ReportDetailPageContainer - Entry point for rendering report detail page in Rails
 */
export function ReportDetailPageContainer(props: ReportDetailPageContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ReportDetailPageInner {...props} />
    </QueryClientProvider>
  );
}

/**
 * Mount function for rendering from Rails
 */
export function mountReportDetailPage(
  container: HTMLElement,
  props: ReportDetailPageContainerProps
) {
  // Clean up existing root if any (for Turbo navigation)
  const existingRoot = (container as any).__reactRoot;
  if (existingRoot) {
    existingRoot.unmount();
  }

  const root = createRoot(container);
  (container as any).__reactRoot = root;
  root.render(<ReportDetailPageContainer {...props} />);
  return root;
}

// Expose mount function to window for Rails integration
if (typeof window !== 'undefined') {
  (window as any).mountReportDetailPage = mountReportDetailPage;
}

export default ReportDetailPageContainer;
